import { isOverwolf, TH_GL_URL } from "./env";
import { HOTKEYS } from "./thgl-app/hotkeys";
import type { MarkerOptions } from "./types";
import type { InGameCoordinates } from "./coordinates";

export const DEFAULT_PATREON_TIER_IDS = [
  "21470801",
  "21470797",
  "21470809",
  "special",
];

export const games: Array<Game> = [
  {
    // Vaultbreakers (BetaDwarf / Square Enix Collective, Unity 6). Web map of the free
    // Steam demo (app 4488770): only the demo's map (Wolfswood) ships. Online PvPvE
    // matches → no companion/live tracking. Title MUST match data-forge global-menu.ts.
    id: "vaultbreakers",
    discordId: "vaultbreakers",
    title: "Vaultbreakers",
    steamAppId: 306910,
    logo: `${TH_GL_URL}/global_icons/vaultbreakers.webp`,
    web: "https://vaultbreakers.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    // Title MUST match data-forge global-menu.ts byte-for-byte.
    id: "blood-of-dawnwalker",
    discordId: "blood-of-dawnwalker",
    title: "The Blood of Dawnwalker",
    steamAppId: 3751260,
    logo: `${TH_GL_URL}/global_icons/blood-of-dawnwalker.webp`,
    companion: {
      baseURL: "/apps/blood-of-dawnwalker",
      controllerURL: "/apps/blood-of-dawnwalker/controller",
      desktopURL: "/apps/blood-of-dawnwalker",
      overlayURL: "/apps/blood-of-dawnwalker/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        // Spawns carry UE Z as height (p = [UE.X, UE.Y, Z]); one open world with
        // caves/interiors stacked under the surface, so the z filter is meaningful.
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "The Blood of Dawnwalker",
          // THGLApp game_registry.h + manifests/targets.json use the same name.
          // The exe is Dawnwalker.exe, NOT *-Win64-Shipping.exe.
          processNames: ["Dawnwalker.exe", "Dawnwalker-WinGDK-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://bloodofdawnwalker.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "dragonsword-awakening",
    discordId: "dragonsword-awakening",
    title: "DragonSword: Awakening",
    steamAppId: 4570720,
    logo: `${TH_GL_URL}/global_icons/dragonsword-awakening.webp`,
    additionalFilters: ["DragonSwordSaveImport"],
    companion: {
      baseURL: "/apps/dragonsword-awakening",
      controllerURL: "/apps/dragonsword-awakening/controller",
      desktopURL: "/apps/dragonsword-awakening",
      overlayURL: "/apps/dragonsword-awakening/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "DragonSword: Awakening",
          processNames: ["DSClient-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://dragonswordawakening.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "crimson-desert",
    discordId: "crimson-desert",
    title: "Crimson Desert",
    steamAppId: 3321460,
    logo: `${TH_GL_URL}/global_icons/crimson-desert.webp`,
    additionalFilters: ["CrimsonDesertZones", "CrimsonDesertSaveImport"],
    companion: {
      baseURL: "/apps/crimson-desert",
      controllerURL: "/apps/crimson-desert/controller",
      desktopURL: "/apps/crimson-desert",
      overlayURL: "/apps/crimson-desert/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 100,
          zDistance: 5,
        },
        clusterPrecision: 10,
      },
      games: [
        {
          title: "Crimson Desert",
          processNames: ["CrimsonDesert.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://crimsondesert.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "starsand-island",
    discordId: "starsand-island",
    title: "Starsand Island",
    steamAppId: 2966320,
    logo: `${TH_GL_URL}/global_icons/starsand-island.webp`,
    companion: {
      baseURL: "/apps/starsand-island",
      controllerURL: "/apps/starsand-island/controller",
      desktopURL: "/apps/starsand-island",
      overlayURL: "/apps/starsand-island/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 100,
          zDistance: 5,
        },
      },
      games: [
        {
          title: "Starsand Island",
          processNames: ["StarsandIsland.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://starsandisland.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "soulmask",
    discordId: "soulmask",
    title: "Soulmask",
    steamAppId: 2646460,
    logo: `${TH_GL_URL}/global_icons/soulmask.webp`,
    companion: {
      baseURL: "/apps/soulmask",
      controllerURL: "/apps/soulmask/controller",
      desktopURL: "/apps/soulmask",
      overlayURL: "/apps/soulmask/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Soulmask",
          processNames: ["WS-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://soulmask.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "legend-of-khiimori",
    discordId: "legend-of-khiimori",
    title: "The Legend of Khiimori",
    steamAppId: 2697000,
    logo: `${TH_GL_URL}/global_icons/legend-of-khiimori.webp`,
    companion: {
      baseURL: "/apps/legend-of-khiimori",
      controllerURL: "/apps/legend-of-khiimori/controller",
      desktopURL: "/apps/legend-of-khiimori",
      overlayURL: "/apps/legend-of-khiimori/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "The Legend of Khiimori",
          processNames: ["LegendOfKhiimori-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://khiimori.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "sinking-city-2",
    discordId: "sinking-city-2",
    title: "The Sinking City 2",
    steamAppId: 2825860,
    logo: `${TH_GL_URL}/global_icons/sinking-city-2.webp`,
    web: "https://sinkingcity2.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    // The First Descendant (Nexon). No companion block: online-only client with anti-cheat, so
    // there is no memory reading / live tracking — web map + database only.
    id: "the-first-descendant",
    discordId: "the-first-descendant",
    title: "The First Descendant",
    steamAppId: 2074920,
    logo: `${TH_GL_URL}/global_icons/the-first-descendant.webp`,
    web: "https://thefirstdescendant.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    // AION 2 (NCSoft). Kernel-driver reads past NCGuard verified live on 2026-10-05 (Steam
    // build 25719316): position, map name and heading are correct in World_L_Starter. Scope:
    // the local player plus live gathering nodes and monsters on the faction overworld maps,
    // and collected Empyrean Traces, enforced in the THGLApp detector (Mixed game, fair-play
    // R1/R2/R3).
    id: "aion2",
    discordId: "aion2",
    title: "AION 2",
    steamAppId: 3393110,
    logo: `${TH_GL_URL}/global_icons/aion2.webp`,
    companion: {
      baseURL: "/apps/aion2",
      controllerURL: "/apps/aion2/controller",
      desktopURL: "/apps/aion2",
      overlayURL: "/apps/aion2/overlay",
      markerOptions: {
        radius: 6,
        // data-forge public\aion2\icons\player.webp: the world-map arrow, saved turned to
        // point right.
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "AION 2",
          // Steam build: a stub and the real game both run as AION2.exe; THGLApp picks the
          // child with the window (verified 2026-10-05). NCSoft Purple build's exe not checked.
          processNames: ["AION2.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://aion2.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "soulframe",
    discordId: "soulframe",
    title: "Soulframe",
    steamAppId: 4095380,
    logo: `${TH_GL_URL}/global_icons/soulframe.webp`,
    web: "https://soulframe.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "where-winds-meet",
    discordId: "where-winds-meet",
    title: "Where Winds Meet",
    steamAppId: 3564740,
    logo: `${TH_GL_URL}/global_icons/where-winds-meet.webp`,
    companion: {
      // Public since 2026-09-28 (was an Elite-gated preview release via
      // PREVIEW_RELEASE_APPS from 2026-09-13). (Was inDevelopment — fully hidden — until the
      // detector got real offsets: player position and heading now come from a
      // code-anchored signature and were verified in-game, so live mode no longer
      // reports a player at the world origin.)
      baseURL: "/apps/where-winds-meet",
      controllerURL: "/apps/where-winds-meet/controller",
      desktopURL: "/apps/where-winds-meet",
      overlayURL: "/apps/where-winds-meet/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        // Spawns carry a real height (p = [lat, lng, height] = [-X, -Z, Y]),
        // so the z filter is meaningful here. The world spans 12096 units on
        // both axes and world Y runs roughly -2160..520 across all placements.
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Where Winds Meet",
          processNames: ["wwm.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://wherewindsmeet.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "souls-remnant",
    discordId: "souls-remnant",
    title: "Soul's Remnant",
    steamAppId: 3451980,
    logo: `${TH_GL_URL}/global_icons/souls-remnant.webp`,
    web: "https://soulsremnant.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "welcome-to-elderfield",
    discordId: "welcome-to-elderfield",
    title: "Welcome to Elderfield",
    steamAppId: 3195440,
    logo: `${TH_GL_URL}/global_icons/welcome-to-elderfield.webp`,
    web: "https://welcometoelderfield.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    // The Witcher 3: Wild Hunt — Remastered (CD PROJEKT RED, REDengine 3). Web map first; no
    // companion block yet (live tracking not built yet).
    id: "the-witcher-3",
    discordId: "the-witcher-3",
    title: "The Witcher 3: Wild Hunt",
    steamAppId: 292030,
    logo: `${TH_GL_URL}/global_icons/the-witcher-3.webp`,
    companion: {
      baseURL: "/apps/the-witcher-3",
      controllerURL: "/apps/the-witcher-3/controller",
      desktopURL: "/apps/the-witcher-3",
      overlayURL: "/apps/the-witcher-3/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        // Live containers come from the same placed entity as the static marker (identical
        // position), typed by template path: a looted (empty) one marks its marker discovered.
        liveConfirmRadius: 1,
        zPos: {
          xyMaxDistance: 50,
          zDistance: 30,
        },
      },
      games: [
        {
          title: "The Witcher 3: Wild Hunt",
          processNames: ["witcher3.exe"],
        },
      ],
      // F5 / F9 are the game's fixed quicksave / quickload keys - never bind them here.
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F3",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F4",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F3",
      },
    },
    web: "https://witcher3.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    // Minecraft Dungeons II (Mojang / Double Eleven). Web map + database first; no companion
    // block yet (live tracking not built yet).
    id: "minecraft-dungeons-2",
    discordId: "minecraft-dungeons-2",
    title: "Minecraft Dungeons II",
    steamAppId: 1912410,
    logo: `${TH_GL_URL}/global_icons/minecraft-dungeons-2.webp`,
    web: "https://minecraftdungeons2.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    // Baldur's Gate: Enhanced Edition + Siege of Dragonspear (Beamdog, Infinity Engine EE).
    // Live mode: THGLApp reads the engine's own object table (offline single-player).
    id: "baldurs-gate-ee",
    discordId: "baldurs-gate-ee",
    title: "Baldur's Gate: Enhanced Edition",
    steamAppId: 228280,
    logo: `${TH_GL_URL}/global_icons/baldurs-gate-ee.webp`,
    companion: {
      baseURL: "/apps/baldurs-gate-ee",
      controllerURL: "/apps/baldurs-gate-ee/controller",
      desktopURL: "/apps/baldurs-gate-ee",
      overlayURL: "/apps/baldurs-gate-ee/overlay",
      markerOptions: {
        // 10% above the usual 6: the small in-game sprites and round creature markers read better.
        radius: 6.6,
        // Party game: the live player is the CAMERA (not drawn); members get portrait markers.
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 50,
          zDistance: 1000,
        },
      },
      games: [
        {
          title: "Baldur's Gate: Enhanced Edition",
          processNames: ["Baldur.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://baldursgateee.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "albion-online",
    discordId: "albion-online",
    title: "Albion Online",
    steamAppId: 761890,
    logo: `${TH_GL_URL}/global_icons/albion-online.webp`,
    web: "https://albiononline.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "delta-force",
    discordId: "delta-force",
    title: "Delta Force",
    steamAppId: 2507950,
    logo: `${TH_GL_URL}/global_icons/delta-force.webp`,
    web: "https://deltaforce.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "starrupture",
    discordId: "starrupture",
    title: "Star Rupture",
    steamAppId: 1631270,
    logo: `${TH_GL_URL}/global_icons/starrupture.webp`,
    companion: {
      baseURL: "/apps/starrupture",
      controllerURL: "/apps/starrupture/controller",
      desktopURL: "/apps/starrupture",
      overlayURL: "/apps/starrupture/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Star Rupture",
          processNames: ["StarRuptureGameSteam-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://starrupture.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "subnautica-2",
    discordId: "subnautica-2",
    title: "Subnautica 2",
    steamAppId: 1962700,
    logo: `${TH_GL_URL}/global_icons/subnautica-2.webp`,
    companion: {
      baseURL: "/apps/subnautica-2",
      controllerURL: "/apps/subnautica-2/controller",
      desktopURL: "/apps/subnautica-2",
      overlayURL: "/apps/subnautica-2/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        // Underwater: large vertical range, so fade markers by depth.
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Subnautica 2",
          processNames: [
            "Subnautica2-Win64-Shipping.exe",
            "Subnautica2-WinGDK-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://subnautica2.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "planet-crafter",
    discordId: "planet-crafter",
    title: "The Planet Crafter",
    steamAppId: 1284190,
    logo: `${TH_GL_URL}/global_icons/planet-crafter.webp`,
    companion: {
      baseURL: "/apps/planet-crafter",
      controllerURL: "/apps/planet-crafter/controller",
      desktopURL: "/apps/planet-crafter",
      overlayURL: "/apps/planet-crafter/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 400,
          zDistance: 60,
        },
      },
      games: [
        {
          title: "The Planet Crafter",
          processNames: ["Planet Crafter.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://planetcrafter.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "witchspire",
    discordId: "witchspire",
    title: "Witchspire",
    steamAppId: 2679100,
    logo: `${TH_GL_URL}/global_icons/witchspire.webp`,
    companion: {
      baseURL: "/apps/witchspire",
      controllerURL: "/apps/witchspire/controller",
      desktopURL: "/apps/witchspire",
      overlayURL: "/apps/witchspire/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Witchspire",
          processNames: ["Hercules-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://witchspire.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "conan-exiles",
    discordId: "conan-exiles",
    title: "Conan Exiles Enhanced",
    steamAppId: 440900,
    logo: `${TH_GL_URL}/global_icons/conan-exiles.webp`,
    companion: {
      baseURL: "/apps/conan-exiles",
      controllerURL: "/apps/conan-exiles/controller",
      desktopURL: "/apps/conan-exiles",
      overlayURL: "/apps/conan-exiles/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 270,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Conan Exiles Enhanced",
          processNames: ["ConanSandbox-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://conanexiles.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "gothic-1-remake",
    discordId: "gothic-1-remake",
    title: "Gothic 1 Remake",
    steamAppId: 1297900,
    logo: `${TH_GL_URL}/global_icons/gothic-1-remake.webp`,
    companion: {
      baseURL: "/apps/gothic-1-remake",
      controllerURL: "/apps/gothic-1-remake/controller",
      desktopURL: "/apps/gothic-1-remake",
      overlayURL: "/apps/gothic-1-remake/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 270,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
        clusterPrecision: 5,
      },
      games: [
        {
          title: "Gothic 1 Remake",
          processNames: ["G1R-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://gothic1remake.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "duet-night-abyss",
    discordId: "duet-night-abyss",
    title: "Duet Night Abyss",
    steamAppId: 3950020,
    logo: `${TH_GL_URL}/global_icons/duetnightabyss.webp`,
    companion: {
      baseURL: "/apps/duet-night-abyss",
      controllerURL: "/apps/duet-night-abyss/controller",
      desktopURL: "/apps/duet-night-abyss",
      overlayURL: "/apps/duet-night-abyss/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 5000,
          zDistance: 100,
        },
      },
      games: [
        {
          title: "Duet Night Abyss",
          processNames: ["EM-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://duetnightabyss.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "blue-protocol-star-resonance",
    discordId: "blue-protocol-star-resonance",
    title: "Blue Protocol: Star Resonance",
    steamAppId: 3681810,
    logo: `${TH_GL_URL}/global_icons/starresonance.webp`,
    companion: {
      baseURL: "/apps/blue-protocol-star-resonance",
      controllerURL: "/apps/blue-protocol-star-resonance/controller",
      desktopURL: "/apps/blue-protocol-star-resonance",
      overlayURL: "/apps/blue-protocol-star-resonance/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 100,
          zDistance: 5,
        },
      },
      games: [
        {
          title: "Blue Protocol: Star Resonance",
          processNames: [
            "Star.exe",
            "StarASIA.exe",
            "StarSEA.exe",
            "BPSR_STEAM.exe",
            "BPSR.exe",
            "BPSR_EPIC.exe",
            "StarASIA_STEAM.exe",
            "應用程式.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://starresonance.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "chrono-odyssey",
    discordId: "chrono-odyssey",
    title: "Chrono Odyssey",
    steamAppId: 2873440,
    logo: `${TH_GL_URL}/global_icons/chrono-odyssey.webp`,
    web: "https://chronoodyssey.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "dune-awakening",
    discordId: "dune-awakening",
    title: "Dune: Awakening",
    steamAppId: 1172710,
    logo: `${TH_GL_URL}/global_icons/dune.webp`,
    lockedWindowComponents: ["DuneDeepDesertGrid", "DuneHeatmaps"],
    additionalFilters: ["DuneDeepDesertGrid", "DuneHeatmaps"],
    additionalTooltip: ["DuneAltitude"],
    companion: {
      baseURL: "/apps/dune-awakening",
      controllerURL: "/apps/dune-awakening/controller",
      desktopURL: "/apps/dune-awakening",
      overlayURL: "/apps/dune-awakening/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Dune: Awakening",
          processNames: [
            "DuneSandbox-Win64-Shipping.exe",
            "DuneSandbox-WinGDK-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://duneawakening.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "palia",
    discordId: "palia",
    title: "Palia",
    steamAppId: 2707930,
    lockedWindowComponents: ["PaliaTime"],
    // PaliaWorldCodeRequest renders nothing — it's an always-mounted driver that
    // toasts the player when someone requests their world's join code.
    additionalComponents: ["PaliaGrid", "PaliaWorldCodeRequest"],
    // PaliaActiveWorlds shows the in-game clock at the end of its row, so
    // there is no separate PaliaTime sidebar entry (the locked-window
    // PaliaTime overlay stays via lockedWindowComponents).
    additionalFilters: [
      "PaliaWeeklyWants",
      "PaliaActiveWorlds",
      "PaliaGridToggle",
    ],
    // Clock + event timetable reachable while the filter panel is hidden.
    filterBarComponents: ["PaliaClockButton"],
    compactOverlay: [
      "CurrentZone",
      "PaliaGridCell",
      "PaliaTime",
      "PaliaWeeklyWants",
    ],
    logo: `${TH_GL_URL}/global_icons/palia.webp`,
    companion: {
      baseURL: "/apps/palia",
      controllerURL: "/apps/palia/controller",
      desktopURL: "/apps/palia",
      overlayURL: "/apps/palia/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 15000,
          zDistance: 400,
        },
        clusterPrecision: 5,
      },
      games: [
        {
          title: "Palia",
          processNames: [
            "PaliaClient-Win64-Shipping.exe",
            "PaliaClientSteam-Win64-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://palia.th.gl",
    overwolf: {
      id: "fgbodfoepckgplklpccjedophlahnjemfdknhfce",
      title: "Palia Map",
      protocol: "thgl-palia-map",
      url: "https://www.overwolf.com/app/Leon_Machens-Palia_Map",
      supportsCopySecret: true,
    },
    patreonTierIDs: ["10151819", ...DEFAULT_PATREON_TIER_IDS],
    partnerApps: [
      {
        id: "palia-tracker",
        title: "Palia Tracker",
        description:
          "Keep track of your daily gifted Palians and weeekly wants.",
        author: "anniebananie",
        web: "https://www.paliatracker.com/",
      },
    ],
  },
  {
    id: "grounded2",
    discordId: "grounded2",
    title: "Grounded 2",
    steamAppId: 2661300,
    logo: `${TH_GL_URL}/global_icons/grounded2.webp`,
    companion: {
      baseURL: "/apps/grounded2",
      controllerURL: "/apps/grounded2/controller",
      desktopURL: "/apps/grounded2",
      overlayURL: "/apps/grounded2/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 180,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Grounded 2",
          processNames: [
            "Grounded2Steam-Win64-Shipping.exe",
            "Grounded2-WinGDK-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://grounded2.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "palworld",
    discordId: "palworld",
    title: "Palworld",
    steamAppId: 1623730,
    logo: `${TH_GL_URL}/global_icons/palworld.webp`,
    additionalTooltip: ["InGameCoordinates"],
    inGameCoordinates: {
      scale: 459,
      offsetX: -158000,
      offsetY: 123888,
      round: true,
    },
    companion: {
      baseURL: "/apps/palworld",
      controllerURL: "/apps/palworld/controller",
      desktopURL: "/apps/palworld",
      overlayURL: "/apps/palworld/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 15000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Palworld",
          processNames: [
            "Palworld-Win64-Shipping.exe",
            "Palworld-WinGDK-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://palworld.th.gl",
    overwolf: {
      id: "ebafpjfhleenmkcmdhlbdchpdalblhiellgfmmbb",
      title: "Palworld Interactive Map",
      protocol: "thgl-palworld",
      url: "https://www.overwolf.com/app/Leon_Machens-Palworld-Interactive-map",
      supportsCopySecret: true,
    },
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "once-human",
    discordId: "once-human",
    title: "Once Human",
    steamAppId: 2139460,
    additionalFilters: ["PlayerDetails"],
    logo: `${TH_GL_URL}/global_icons/once-human.webp`,
    companion: {
      baseURL: "/apps/once-human",
      controllerURL: "/apps/once-human/controller",
      desktopURL: "/apps/once-human",
      overlayURL: "/apps/once-human/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 200,
          zDistance: 3,
        },
        coordinateCopyFormat: "({x},{y})",
      },
      games: [
        {
          title: "Once Human",
          processNames: ["ONCE_HUMAN.exe", "七日世界.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://oncehuman.th.gl",
    overwolf: {
      id: "hjolmidofgehhbnofcpdbcednenibgnblipabcko",
      title: "Once Human Map",
      protocol: "thgl-once-human",
      url: "https://www.overwolf.com/app/Leon_Machens-Once_Human_Map",
      supportsCopySecret: true,
    },
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "infinity-nikki",
    discordId: "infinity-nikki",
    title: "Infinity Nikki",
    steamAppId: 3164330,
    logo: `${TH_GL_URL}/global_icons/infinity-nikki.webp`,
    companion: {
      baseURL: "/apps/infinity-nikki",
      controllerURL: "/apps/infinity-nikki/controller",
      desktopURL: "/apps/infinity-nikki",
      overlayURL: "/apps/infinity-nikki/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Infinity Nikki",
          processNames: ["X6Game-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://infinitynikki.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "rsdragonwilds",
    discordId: "rsdragonwilds",
    title: "RuneScape: Dragonwilds",
    steamAppId: 1374490,
    logo: `${TH_GL_URL}/global_icons/rsdragonwilds.webp`,
    companion: {
      baseURL: "/apps/rsdragonwilds",
      controllerURL: "/apps/rsdragonwilds/controller",
      desktopURL: "/apps/rsdragonwilds",
      overlayURL: "/apps/rsdragonwilds/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "RuneScape: Dragonwilds",
          processNames: [
            "RSDragonwilds-Win64-Shipping.exe",
            "RSDragonwilds-WinGDK-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://dragonwilds.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "aniimo",
    discordId: "aniimo",
    title: "Aniimo",
    steamAppId: 4126040,
    logo: `${TH_GL_URL}/global_icons/aniimo.webp`,
    companion: {
      baseURL: "/apps/aniimo",
      controllerURL: "/apps/aniimo/controller",
      desktopURL: "/apps/aniimo",
      overlayURL: "/apps/aniimo/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 100,
          zDistance: 5,
        },
      },
      games: [
        {
          title: "Aniimo",
          processNames: ["Aniimo.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://aniimo.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "avowed",
    discordId: "avowed",
    title: "Avowed",
    steamAppId: 2457220,
    logo: `${TH_GL_URL}/global_icons/avowed.webp`,
    companion: {
      baseURL: "/apps/avowed",
      controllerURL: "/apps/avowed/controller",
      desktopURL: "/apps/avowed",
      overlayURL: "/apps/avowed/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Avowed",
          processNames: [
            "Avowed-Win64-Shipping.exe",
            "Avowed-WinGDK-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://avowed.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "satisfactory",
    discordId: "satisfactory",
    title: "Satisfactory",
    steamAppId: 526870,
    logo: `${TH_GL_URL}/global_icons/satisfactory.webp`,
    web: "https://satisfactory.th.gl",
    additionalFilters: ["SatisfactorySeed"],
    companion: {
      baseURL: "/apps/satisfactory",
      controllerURL: "/apps/satisfactory/controller",
      desktopURL: "/apps/satisfactory",
      overlayURL: "/apps/satisfactory/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 15000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Satisfactory",
          processNames: [
            "FactoryGameSteam-Win64-Shipping.exe",
            "FactoryGameEGS-Win64-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    overwolf: {
      id: "mgpcocpamehmkagnkjcbabcnnhbebclkiekekhmg",
      title: "Satisfactory Map",
      protocol: "thgl-satisfactory-map",
      url: "https://www.overwolf.com/app/Leon_Machens-Satisfactory_Map",
      supportsCopySecret: true,
    },
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "wuthering-waves",
    discordId: "wuthering-waves",
    title: "Wuthering Waves",
    steamAppId: 3513350,
    logo: `${TH_GL_URL}/global_icons/wuthering-waves.webp`,
    additionalTooltip: ["InGameCoordinates"],
    // Map position p = [worldY/100, worldX/100] (extraction), so in-game ≈ map/100.
    inGameCoordinates: { scale: 100, round: true },
    companion: {
      baseURL: "/apps/wuthering-waves",
      controllerURL: "/apps/wuthering-waves/controller",
      desktopURL: "/apps/wuthering-waves",
      overlayURL: "/apps/wuthering-waves/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 15000,
          zDistance: 400,
        },
        clusterPrecision: 50,
      },
      games: [
        {
          title: "Wuthering Waves",
          processNames: ["Client-Win64-Shipping.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://wuthering.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "neverness-to-everness",
    discordId: "neverness-to-everness",
    title: "Neverness To Everness",
    steamAppId: 4508340,
    logo: `${TH_GL_URL}/global_icons/neverness-to-everness.webp`,
    companion: {
      baseURL: "/apps/neverness-to-everness",
      controllerURL: "/apps/neverness-to-everness/controller",
      desktopURL: "/apps/neverness-to-everness",
      overlayURL: "/apps/neverness-to-everness/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10000,
          zDistance: 1000,
        },
      },
      games: [
        {
          title: "Neverness To Everness",
          processNames: ["HTGame.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://nte.th.gl",
    markerOptions: {
      radius: 6,
      playerIcon: "player.webp",
      playerIconForward: 90,
      imageSprite: true,
    },
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "diablo4",
    discordId: "diablo4",
    title: "Diablo IV",
    steamAppId: 2344520,
    logo: `${TH_GL_URL}/global_icons/diablo4.webp`,
    web: "https://diablo4.th.gl",
    markerOptions: {
      radius: 6,
      playerIcon: "player.webp",
      imageSprite: true,
      zPos: {
        xyMaxDistance: 10,
        zDistance: 2,
      },
    },
    companion: {
      baseURL: "/apps/diablo4",
      controllerURL: "/apps/diablo4/controller",
      desktopURL: "/apps/diablo4",
      overlayURL: "/apps/diablo4/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 10,
          zDistance: 2,
        },
      },
      games: [
        {
          title: "Diablo IV",
          processNames: ["Diablo IV.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    overwolf: {
      id: "olbbpfjombddiijdbjeeegeclifleaifdeonllfd",
      title: "Diablo 4 Map",
      protocol: "thgl-diablo4-map",
      url: "https://www.overwolf.com/app/Leon_Machens-Diablo_4_Map",
      supportsCopySecret: true,
    },
    patreonTierIDs: ["9878731", ...DEFAULT_PATREON_TIER_IDS],
    partnerApps: [
      {
        id: "diablo4-companion",
        title: "Diablo IV Companion App",
        description:
          "Ingame overlay to help you find your perfect gear affixes.",
        author: "Uthar",
        web: "https://github.com/josdemmers/Diablo4Companion",
      },
    ],
  },
  {
    id: "new-world",
    discordId: "aeternum-map",
    title: "New World",
    steamAppId: 1063730,
    logo: `${TH_GL_URL}/global_icons/aeternum-map.png`,
    web: "https://aeternum-map.th.gl",
    overwolf: {
      id: "bemfloapmmjpmdmjfjgegnacdlgeapmkcmcmceei",
      title: "Aeternum Map",
      protocol: "thgl-aeternum-map",
      url: "https://www.overwolf.com/app/Leon_Machens-Aeternum_Map",
    },
    patreonTierIDs: ["2304899", ...DEFAULT_PATREON_TIER_IDS],
    premiumFeatures: [
      "Open Minimized",
      "Open aeternum-map.th.gl on game launch.",
    ],
    partnerApps: [
      {
        id: "new-world-companion",
        title: "New World Companion App",
        description: "Track learned recipes and current trading post prices.",
        author: "Uthar",
        web: "https://github.com/josdemmers/NewWorldCompanion",
      },
    ],
  },
  {
    id: "sons-of-the-forest",
    discordId: "sons-of-the-forest-map",
    title: "Sons Of The Forest",
    steamAppId: 1326470,
    logo: `${TH_GL_URL}/global_icons/sons-of-the-forest.webp`,
    web: "https://sotf.th.gl",
    overwolf: {
      id: "kkgfkggfpkmndnadgkegoheijamdjkeagojfbbfn",
      title: "Sons Of The Forest Map",
      protocol: "thgl-sotf-map",
      url: "https://www.overwolf.com/app/Leon_Machens-Sons_Of_The_Forest_Map",
    },
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "pax-dei",
    discordId: "pax-dei",
    title: "Pax Dei",
    steamAppId: 1995520,
    logo: `${TH_GL_URL}/global_icons/pax-dei.webp`,
    companion: {
      // INVITE ONLY (2026-09-08): the publisher refused a public companion
      // app, so this replaces the discontinued Overwolf app for a hand-picked
      // set of Patreon accounts (`app_invites` table → account.invites). Never
      // advertised (www lists/counts/OG, tenant appUrl stays null); shown in the
      // THGLApp dashboard + auto-opened only for invited accounts, and the app
      // route renders the InviteOnlyGate for everyone else. The detector's
      // actors-api tracking runs for every THGLApp user regardless.
      inviteOnly: true,
      baseURL: "/apps/pax-dei",
      controllerURL: "/apps/pax-dei/controller",
      desktopURL: "/apps/pax-dei",
      overlayURL: "/apps/pax-dei/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 15000,
          zDistance: 350,
        },
      },
      games: [
        {
          title: "Pax Dei",
          processNames: [
            "PaxDeiClient-Win64-Shipping.exe",
            "NWXClient-Win64-Shipping.exe",
          ],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://paxdei.th.gl",
    markerOptions: {
      radius: 6,
      playerIcon: "player.webp",
      playerIconForward: 90,
      imageSprite: true,
    },
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "hogwarts-legacy",
    discordId: "hogwarts-legacy-map",
    title: "Hogwarts Legacy",
    steamAppId: 990080,
    logo: `${TH_GL_URL}/global_icons/hogwarts-legacy.webp`,
    companion: {
      baseURL: "/apps/hogwarts-legacy",
      controllerURL: "/apps/hogwarts-legacy/controller",
      desktopURL: "/apps/hogwarts-legacy",
      overlayURL: "/apps/hogwarts-legacy/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        playerIconForward: 90,
        imageSprite: true,
        zPos: {
          xyMaxDistance: 15000,
          zDistance: 350,
        },
      },
      games: [
        {
          title: "Hogwarts Legacy",
          processNames: ["HogwartsLegacy.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://hogwarts.th.gl",
    markerOptions: {
      radius: 6,
      playerIcon: "player.webp",
      playerIconForward: 90,
      imageSprite: true,
      zPos: {
        xyMaxDistance: 15000,
        zDistance: 350,
      },
    },
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "night-crows",
    discordId: "night-crows",
    title: "Night Crows",
    logo: `${TH_GL_URL}/global_icons/night-crows.webp`,
    web: "https://nightcrows.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "graveyard-keeper-2",
    discordId: "graveyard-keeper-2",
    title: "Graveyard Keeper 2",
    steamAppId: 4358690,
    logo: `${TH_GL_URL}/global_icons/graveyard-keeper-2.webp`,
    companion: {
      baseURL: "/apps/graveyard-keeper-2",
      controllerURL: "/apps/graveyard-keeper-2/controller",
      desktopURL: "/apps/graveyard-keeper-2",
      overlayURL: "/apps/graveyard-keeper-2/overlay",
      markerOptions: {
        radius: 6,
        // the game's own map pin (upright, not an arrow) — the detector sends r = 0
        playerIcon: "player.webp",
        imageSprite: true,
        // world is small (~260 x 320 units for the whole overworld)
        zPos: {
          xyMaxDistance: 60,
          zDistance: 10,
        },
      },
      games: [
        {
          title: "Graveyard Keeper 2",
          processNames: ["GraveyardKeeper2.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://graveyardkeeper2.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "valheim",
    discordId: "valheim",
    title: "Valheim",
    steamAppId: 892970,
    logo: `${TH_GL_URL}/global_icons/valheim.webp`,
    web: "https://valheim.th.gl",
    // Per-seed procedural world: the seed panel + the worker-rendered seed map
    additionalFilters: ["ValheimSeed"],
    additionalComponents: ["ValheimSeedMap"],
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "songs-of-conquest",
    discordId: "songs-of-conquest",
    title: "Songs of Conquest",
    steamAppId: 867210,
    logo: `${TH_GL_URL}/global_icons/songs-of-conquest.webp`,
    web: "https://soc.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "heartopia",
    discordId: "heartopia",
    title: "Heartopia",
    steamAppId: 4025700,
    logo: `${TH_GL_URL}/global_icons/heartopia.webp`,
    companion: {
      baseURL: "/apps/heartopia",
      controllerURL: "/apps/heartopia/controller",
      desktopURL: "/apps/heartopia",
      overlayURL: "/apps/heartopia/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        zPos: {
          xyMaxDistance: 100,
          zDistance: 5,
        },
      },
      games: [
        {
          title: "Heartopia",
          processNames: ["xdt.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://heartopia.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "homm-olden-era",
    discordId: "homm-olden-era",
    title: "Heroes of Might & Magic: Olden Era",
    steamAppId: 3105440,
    logo: `${TH_GL_URL}/global_icons/homm-olden-era.webp`,
    web: "https://oldenera.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
  {
    id: "enshrouded",
    discordId: "enshrouded",
    title: "Enshrouded",
    steamAppId: 1203620,
    logo: `${TH_GL_URL}/global_icons/enshrouded.webp`,
    companion: {
      // Fully public (2026-09-13): website + in-game companion open to everyone. Was
      // inDevelopment (hidden), then companion-only preview (Elite-gated app) until live
      // chest/item tracking landed; now ungated.
      baseURL: "/apps/enshrouded",
      controllerURL: "/apps/enshrouded/controller",
      desktopURL: "/apps/enshrouded",
      overlayURL: "/apps/enshrouded/overlay",
      markerOptions: {
        radius: 6,
        playerIcon: "player.webp",
        imageSprite: true,
        // Live detector emits generic chest/supply (tier isn't in the ECS archetype), so a live
        // actor confirms & hides the fine-grained static tier marker (gold_chest/…) at its spot.
        // 5 m ≈ just above the sint64→m rounding; chests/pickups sit farther apart than this.
        liveConfirmRadius: 5,
        zPos: {
          // Placeholder values — update once sint64→float coordinate scale
          // is confirmed (likely mm→m: divide by 1000) and tested in-game.
          xyMaxDistance: 10000,
          zDistance: 400,
        },
      },
      games: [
        {
          title: "Enshrouded",
          processNames: ["enshrouded.exe"],
        },
      ],
      defaultHotkeys: {
        [HOTKEYS.TOGGLE_APP]: "F6",
        [HOTKEYS.TOGGLE_LOCK_APP]: "F9",
        [HOTKEYS.ZOOM_IN_APP]: "F7",
        [HOTKEYS.ZOOM_OUT_APP]: "F8",
        [HOTKEYS.DISCOVER_NODE]: "F10",
        [HOTKEYS.TOGGLE_LIVE_MODE]: "F5",
        [HOTKEYS.TOGGLE_OVERLAY_FULLSCREEN]: "SHIFT+F9",
      },
    },
    web: "https://enshrouded.th.gl",
    patreonTierIDs: DEFAULT_PATREON_TIER_IDS,
  },
];

export type PartnerApp = {
  id: string;
  title: string;
  description?: string;
  author: string;
  web: string;
};

export type AdditionalContent =
  | "PlayerDetails"
  | "PaliaActiveWorlds"
  | "PaliaWorldCodeRequest"
  | "PaliaWeeklyWants"
  | "PaliaGrid"
  | "PaliaGridToggle"
  | "PaliaTime"
  | "PaliaClockButton"
  | "DuneDeepDesertGrid"
  | "DuneHeatmaps"
  | "CrimsonDesertZones"
  | "CrimsonDesertSaveImport"
  | "DragonSwordSaveImport"
  | "SatisfactorySeed"
  | "ValheimSeed"
  | "ValheimSeedMap";

export type AdditionalTooltip = "InGameCoordinates" | "DuneAltitude";

/**
 * Widgets for the overlay's "Widgets Only" mode. Generic ones work for every
 * game ("CurrentZone": the player's map + area); game-specific ones carry the
 * game prefix.
 */
export type CompactOverlayWidget =
  | "CurrentZone"
  | "PaliaGridCell"
  | "PaliaTime"
  | "PaliaWeeklyWants";

export type Game = {
  id: string;
  discordId: string;
  title: string;
  /**
   * Steam app id, when the game is on Steam. Seeds the www.th.gl game stats
   * (player counts, patch history) for supported games.
   */
  steamAppId?: number;
  logo: string;
  lockedWindowComponents?: Array<AdditionalContent>;
  additionalComponents?: Array<AdditionalContent>;
  additionalFilters?: Array<AdditionalContent>;
  /**
   * Compact widgets shown next to the floating "Filters" pill while the filter
   * panel is hidden, so a player can keep one open without the whole sidebar
   * (e.g. Palia's event timetable).
   */
  filterBarComponents?: Array<AdditionalContent>;
  /**
   * Widgets of the overlay's "Widgets Only" mode (map hidden, a small movable
   * panel instead). The mode is offered only for games that list widgets
   * here, in this order. Elite Supporter preview. See compact-overlay.ts.
   */
  compactOverlay?: Array<CompactOverlayWidget>;
  additionalTooltip?: Array<AdditionalTooltip>;
  /**
   * Per-game map<->in-game coordinate transform. Enables the "In-Game"
   * coordinate tooltip (with `additionalTooltip: ["InGameCoordinates"]`) and the
   * map-controls "Go to coordinate" in-game input toggle.
   */
  inGameCoordinates?: InGameCoordinates;
  /**
   * Canonical marker render options for this game. For companion games these
   * historically live under `companion.markerOptions`; this top-level field is
   * for games that need marker options without a companion block (e.g. diablo4,
   * pax-dei, hogwarts-legacy). Read via `getGameMarkerOptions(game)`, which
   * prefers this field and falls back to `companion.markerOptions`.
   */
  markerOptions?: MarkerOptions;
  companion?: {
    /**
     * Companion integration exists but isn't released yet: excluded from
     * "supported games" lists, counts, and badges (companion-app page, www
     * home, game cards, OG image). Direct routing (/apps/<id>, process
     * detection) stays live so the integration can be tested.
     */
    inDevelopment?: boolean;
    /**
     * Companion is finished but INVITE ONLY: never advertised (same public
     * exclusions as `inDevelopment`), yet released for accounts whose
     * server-resolved `invites` include this game id — they get the dashboard
     * card, running-game detection and auto-open. Everyone else who reaches
     * the /apps/<id> route sees the InviteOnlyGate. Invites are managed at
     * www.th.gl/admin/invites. Check access with `isCompanionAccessible`.
     */
    inviteOnly?: boolean;
    baseURL: string;
    controllerURL: string;
    desktopURL: string;
    overlayURL: string;
    markerOptions: {
      radius: number;
      playerIcon: string;
      // Direction the player icon IMAGE points at rotation 0, in degrees clockwise from
      // up (0 = up, 90 = right, 180 = down, 270 = left). The marker is drawn at the
      // game's heading `r`, so the player's on-screen facing is `r + playerIconForward`;
      // "Rotate map with player" needs it to put that direction at the top. Check the
      // icon in public/<game>/icons/player.webp when adding a game. Default 0.
      playerIconForward?: number;
      imageSprite: boolean;
      zPos: {
        xyMaxDistance: number;
        zDistance: number;
      };
      clusterPrecision?: number;
      coordinateCopyFormat?: string;
      // World-unit radius for position-based, type-agnostic live↔predicted dedup: a live actor
      // hides the nearest combined-muted predicted static spawn at its spot (so combined mode shows
      // one marker, not the faded prediction under it). Use when the memory detector emits COARSER
      // types than the static data (e.g. Enshrouded live "chest" vs static gold_chest/silver_chest).
      // 0/undefined = off (default; other games unchanged). Keep it small — just above the
      // live-vs-file coordinate rounding — so it can't swallow a distinct neighbouring spawn.
      liveConfirmRadius?: number;
    };
    games: {
      title: string;
      processNames: string[];
    }[];
    defaultHotkeys: Record<string, string>;
  };
  web?: string;
  overwolf?: {
    id: string;
    title: string;
    protocol: string;
    url: string;
    supportsCopySecret?: boolean;
  };
  patreonTierIDs: string[];
  premiumFeatures?: string[];
  partnerApps?: PartnerApp[];
};

/**
 * True when the game's companion integration is RELEASED — use for
 * "supported games" lists, counts, and badges. Games with
 * `companion.inDevelopment` keep their routing/config but aren't advertised.
 *
 * In DEV mode (`NODE_ENV !== "production"`), in-development companions count as
 * released so they can be tested end-to-end in the THGLApp (listed in the
 * sidebar, running-game detection, overlay/desktop auto-open) while staying
 * hidden in production builds.
 */
export function hasReleasedCompanion(game: Game): boolean {
  if (!game.companion) return false;
  if (process.env.NODE_ENV !== "production") return true;
  return !game.companion.inDevelopment && !game.companion.inviteOnly;
}

/** True when the game's companion is `inviteOnly` (see the type docs). */
export function isInviteOnlyCompanion(game: Game): boolean {
  return !!game.companion?.inviteOnly;
}

/**
 * Per-ACCOUNT companion availability — use wherever the signed-in user's
 * own list of games is built (THGLApp dashboard sidebar/home, process
 * auto-open): every publicly released companion, PLUS `inviteOnly` ones the
 * account is invited to (`useAccountStore().invites`, server-resolved). Public
 * marketing surfaces keep using `hasReleasedCompanion`, which never includes
 * invite-only games.
 */
export function isCompanionAccessible(game: Game, invites: string[]): boolean {
  if (!game.companion) return false;
  if (hasReleasedCompanion(game)) return true;
  if (game.companion.inDevelopment) return false;
  return !!game.companion.inviteOnly && invites.includes(game.id);
}

/**
 * Dashboard order: games the user has played (THGLApp `lastPlayed`, most recent
 * first) on top, everything else in the natural registry order (newest
 * integrations first). Stable for games without a timestamp.
 */
export function sortGamesByLastPlayed(
  list: Game[],
  lastPlayed: Record<string, number>,
): Game[] {
  return list
    .map((game, index) => ({ game, index, at: lastPlayed[game.id] ?? 0 }))
    .sort((a, b) => (a.at !== b.at ? b.at - a.at : a.index - b.index))
    .map((entry) => entry.game);
}

/** How the dashboard sidebar orders its games list. */
export type GamesSort = "recent" | "alpha";

/**
 * Dashboard order for a chosen sort. "recent" is the long-standing default
 * (see sortGamesByLastPlayed); "alpha" sorts by `Game.title`. The titles are
 * NOT localized — every UI language sees the same English strings — only the
 * collation rules are locale-aware (e.g. where "Ä" sorts relative to "A"/"Z").
 * An unknown `sort` value (a stale persisted store) falls back to "recent".
 */
export function sortGamesBy(
  list: Game[],
  sort: GamesSort,
  lastPlayed: Record<string, number>,
  locale: string,
): Game[] {
  if (sort !== "alpha") {
    return sortGamesByLastPlayed(list, lastPlayed);
  }
  const collator = new Intl.Collator(locale, {
    sensitivity: "base",
    numeric: true,
  });
  return [...list].sort((a, b) => collator.compare(a.title, b.title));
}

/**
 * Split a games list into the user's favourites and the rest, each keeping the
 * order of the input list. Ids that are no longer in the registry are ignored.
 */
export function partitionFavoriteGames(
  list: Game[],
  favoriteGames: string[],
): { favorites: Game[]; rest: Game[] } {
  const favoriteIds = new Set(favoriteGames);
  return {
    favorites: list.filter((game) => favoriteIds.has(game.id)),
    rest: list.filter((game) => !favoriteIds.has(game.id)),
  };
}

/** The web subdomain for a game (e.g. "starresonance"), derived from `web`. */
export function getAppDomain(game: Game): string {
  return game.web ? new URL(game.web).host.split(".")[0] : game.id;
}

/**
 * Resolve the current game's canonical `Game.id` from the runtime context.
 * Works across all three places we render, and ALWAYS returns the canonical
 * id (not a surface-specific alias) so per-game data keyed by it — community
 * and personal filters, settings — agrees across surfaces:
 *
 *   - Game tenant on the web (paxdei.th.gl, *.localhost): the first subdomain
 *     is the `AppConfig` domain, which differs from `Game.id` for ~17 games
 *     (e.g. `starresonance` → `blue-protocol-star-resonance`). Mapped back to
 *     the canonical id via the registry. Skips infra subdomains (www/app).
 *   - THGLApp WebView (app.th.gl/apps/<id>): the path segment after `/apps/`
 *     is already the `Game.id`.
 *   - Overwolf extension (overwolf-extension://<extId>): hostname is the
 *     extension id; look it up in the registry to recover the canonical id.
 *
 * Returns null when running SSR, on www/app roots, or when no game matches —
 * callers should treat null as "don't render game-scoped UI" rather than
 * guessing.
 */
/**
 * Extract the app id from an in-app route (`/apps/<id>`), tolerating a locale
 * prefix (`/{locale}/apps/<id>` — every locale except the default `en` is
 * prefixed). Keying off a fixed path position broke all non-English languages:
 * they fell back to generic behavior/storage while English got the per-app
 * one, so e.g. settings appeared to change with the selected language.
 */
export function getAppIdFromPathname(pathname: string): string | null {
  const match = pathname.match(
    /^\/(?:[a-z]{2}(?:-[a-zA-Z0-9]{2,4})?\/)?apps\/([^/?#]+)/,
  );
  return match?.[1] ?? null;
}

export function getCurrentGameId(): string | null {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname;
  const appId = getAppIdFromPathname(path);
  if (appId) return appId;
  if (isOverwolf) {
    const ext = window.location.hostname;
    return games.find((g) => g.overwolf?.id === ext)?.id ?? null;
  }
  // Dev tenants are served as `<domain>-dev.localhost`.
  let sub = window.location.hostname.split(".")[0].replace(/-dev$/, "");
  if (!sub || ["www", "app", "localhost", "127", "0"].includes(sub))
    return null;
  // Map the tenant subdomain back to the canonical id; fall back to the raw
  // subdomain for web-only games with no registry entry (e.g. drakantos).
  return games.find((g) => getAppDomain(g) === sub)?.id ?? sub;
}
